import { useEffect, useRef, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

export function AccessAdminBootstrap({ tenantId, onBusyChange = () => {}, onCreated = () => {} }) {
  const [availability, setAvailability] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [revision, setRevision] = useState(0);
  const [open, setOpen] = useState(false);
  const [fields, setFields] = useState({ name: "", email: "", password: "", confirm_password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const pending = useRef(null);
  const running = useRef(false);
  const mounted = useRef(false);
  const endpoint = `/platform/tenants/${tenantId}/access-admin-bootstrap`;
  const createdCallback = useRef(onCreated);
  createdCallback.current = onCreated;

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; pending.current = null; };
  }, []);
  useEffect(() => {
    let active = true;
    setAvailability(null); setLoadError("");
    api.get(endpoint).then(({ data }) => {
      if (!active) return;
      setAvailability(data);
      if (data.access_ready) createdCallback.current();
    }).catch((e) => { if (active) setLoadError(apiErr(e)); });
    return () => { active = false; };
  }, [endpoint, revision]);

  const clear = () => {
    pending.current = null;
    setFields({ name: "", email: "", password: "", confirm_password: "" });
    setUncertain(false); setError("");
  };
  const close = () => {
    if (running.current) return;
    clear(); setOpen(false); setRevision((v) => v + 1);
  };
  const submit = async (e) => {
    e.preventDefault();
    if (running.current) return;
    if (!pending.current) {
      const name = fields.name.trim(), email = fields.email.trim().toLowerCase();
      if (!name || name.length > 120) { setError("Informe um nome entre 1 e 120 caracteres."); return; }
      if (email.length > 254 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setError("Informe um e-mail válido."); return; }
      if (!fields.password.trim() || Array.from(fields.password).length < 6) { setError("A senha deve ter ao menos 6 caracteres."); return; }
      if (new TextEncoder().encode(fields.password).length > 72) { setError("A senha deve ter no máximo 72 bytes em UTF-8."); return; }
      if (fields.password !== fields.confirm_password) { setError("A senha e a confirmação não conferem."); return; }
      const bytes = new Uint8Array(16);
      window.crypto.getRandomValues(bytes);
      pending.current = {
        key: Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join(""),
        payload: { ...fields, name, email, confirm_tenant_id: tenantId },
      };
    }
    running.current = true; setBusy(true); onBusyChange(true); setError("");
    let completed = false;
    try {
      const { data } = await api.post(endpoint, pending.current.payload,
        { headers: { "Idempotency-Key": pending.current.key } });
      if (!mounted.current) return;
      if (data.access_ready) {
        clear(); setOpen(false); completed = true;
        if (data.audit_pending) toast.warning("Administrador criado e acesso liberado. Auditoria pendente em recuperação automática.");
        else toast.success("Administrador de acesso criado e vinculado à empresa.");
      } else {
        setUncertain(true);
        setError(data.message || "Preparação pendente. Repita a confirmação com os mesmos dados.");
      }
    } catch (e) {
      if (!mounted.current) return;
      if ([400, 401, 403, 404, 409, 422].includes(e?.response?.status)) {
        clear(); setError(apiErr(e));
      } else {
        setUncertain(true);
        setError("Resultado ainda não confirmado. Repita com os mesmos dados. Fechar a janela não desfaz uma operação já enviada.");
      }
    } finally {
      running.current = false;
      if (mounted.current) {
        setBusy(false); onBusyChange(false);
        if (completed) createdCallback.current();
      }
    }
  };

  if (loadError) return <p role="alert" className="text-sm text-amber-200">{loadError} <Button type="button" variant="ghost" onClick={() => setRevision((v) => v + 1)}>Verificar criação de acesso</Button></p>;
  if (!availability) return <p role="status" className="text-sm text-slate-300">Verificando criação de acesso...</p>;
  if (!availability.can_create) return <div className="text-sm text-amber-200" role="status">
    {availability.reason || availability.message}
    {availability.user_id && <Button type="button" variant="ghost" onClick={() => setRevision((v) => v + 1)}>Atualizar recuperação</Button>}
  </div>;

  return <div className="space-y-2">
    <Button type="button" data-testid="access-admin-open" disabled={busy} onClick={() => { clear(); setOpen(true); }}
      className="w-full sm:w-auto whitespace-normal h-auto min-h-10 bg-emerald-700 text-white hover:bg-emerald-800">
      Criar administrador de acesso
    </Button>
    <p className="text-sm text-slate-300">Crie o primeiro acesso administrativo desta empresa. Os dados existentes serão preservados.</p>
    <Dialog open={open} onOpenChange={(value) => { if (!value) close(); }}>
      <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Criar administrador de acesso</DialogTitle>
          <DialogDescription className="text-slate-300">Empresa: {availability.tenant?.name || tenantId}. A nova conta terá acesso administrativo somente a esta empresa.</DialogDescription>
        </DialogHeader>
        <form noValidate onSubmit={submit} data-testid="access-admin-form" className="space-y-4">
          {[["name", "Nome", "text"], ["email", "E-mail", "email"], ["password", "Senha", "password"], ["confirm_password", "Confirmar senha", "password"]].map(([key, label, type]) =>
            <div key={key}><Label htmlFor={"access-admin-" + key}>{label}</Label>
              <Input id={"access-admin-" + key} data-testid={"access-admin-" + key} type={type}
                autoComplete={type === "password" ? "new-password" : "off"}
                value={fields[key]} disabled={busy || uncertain}
                onChange={(e) => setFields((v) => ({ ...v, [key]: e.target.value }))} /></div>)}
          <p className="text-xs text-slate-300">Senha: mínimo de 6 caracteres e máximo de 72 bytes em UTF-8.</p>
          {error && <p role="alert" className="text-sm text-amber-200">{error}</p>}
          <DialogFooter className="gap-2">
            <Button type="button" variant="ghost" disabled={busy} onClick={close}>Cancelar</Button>
            <Button type="submit" data-testid="access-admin-submit" disabled={busy}
              className="whitespace-normal h-auto min-h-10 bg-emerald-700 text-white hover:bg-emerald-800">
              {busy ? "Criando acesso..." : uncertain ? "Repetir confirmação" : "Confirmar criação"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  </div>;
}
